import { useCallback, useEffect, useRef, useState } from "react";

import GenerarUnidadesService from "../../../../../../services/GenerarUnidadesService";
import ModeracionService from "../../../../../../services/ModeracionService";

import { STORAGE_KEY, STORAGE_STEP5 } from "./unitSelectionStep.styles";

const useUnitSelectionStep = ({ onNext, setUnidadesSeleccionadas }) => {
  const [cantidadUnidades, setCantidadUnidades] = useState(8);
  const [unidadesBackend, setUnidadesBackend] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingSituations, setGeneratingSituations] = useState(false);
  const [regeneratingUnits, setRegeneratingUnits] = useState(false);
  const [regeneratingUnitId, setRegeneratingUnitId] = useState(null);
  const [openRegenerateDialog, setOpenRegenerateDialog] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "error", message: "" });
  const hasFetched = useRef(false);

  const toKey = (id) => String(id);
  const normalizeIds = (ids = []) => (Array.isArray(ids) ? ids : []).map(String).sort();

  const showSnack = (message, severity = "error") => {
    setSnack({ open: true, severity, message });
  };

  const closeSnack = (_, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  };

  const getModeracionMessage = (err) => {
    const detail = err?.response?.data?.detail;
    return detail?.message || "Contenido no permitido (malas palabras o +18).";
  };

  const getApiErrorMessage = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    return fallback;
  };

  const persistUnitSelectionState = useCallback(({
    nextCantidadUnidades = cantidadUnidades,
    nextUnidadesBackend = unidadesBackend,
    nextSeleccionados = seleccionados,
  } = {}) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        cantidadUnidades: nextCantidadUnidades,
        unidadesBackend: nextUnidadesBackend,
        seleccionados: (nextSeleccionados || []).map(String),
      })
    );
  }, [cantidadUnidades, unidadesBackend, seleccionados]);

  const clearGeneratedSituationsState = () => {
    localStorage.removeItem(STORAGE_STEP5);
    setUnidadesSeleccionadas([]);
  };

  const regenerateUnitsList = async () => {
    setRegeneratingUnits(true);
    try {
      const nextUnits = await GenerarUnidadesService.generarUnidades(12);
      setUnidadesBackend(nextUnits);
      setSeleccionados([]);
      persistUnitSelectionState({
        nextUnidadesBackend: nextUnits,
        nextSeleccionados: [],
      });
      clearGeneratedSituationsState();
      showSnack("Lista de unidades regenerada correctamente.", "success");
    } catch (error) {
      console.error("Error regenerando unidades:", error);
      showSnack(
        getApiErrorMessage(error, "No se pudo regenerar la lista de unidades."),
        error?.response?.status === 409 ? "warning" : "error"
      );
    } finally {
      setRegeneratingUnits(false);
      setOpenRegenerateDialog(false);
    }
  };

  const regenerateSingleUnit = async (unidad) => {
    setRegeneratingUnitId(unidad.id);

    try {
      const nombresRestantes = unidadesBackend.filter((item) => item.id !== unidad.id).map((item) => item.nombre);
      const nextUnit = await GenerarUnidadesService.regenerarUnidad(nombresRestantes);

      setUnidadesBackend((prev) =>
        prev.map((item) =>
          item.id === unidad.id ? { ...item, nombre: nextUnit.nombre, prioridad: nextUnit.prioridad } : item
        )
      );

      clearGeneratedSituationsState();
      showSnack("Unidad regenerada correctamente.", "success");
    } catch (error) {
      console.error("Error regenerando unidad:", error);
      showSnack(
        getApiErrorMessage(error, "No se pudo regenerar la unidad seleccionada."),
        error?.response?.status === 409 ? "warning" : "error"
      );
    } finally {
      setRegeneratingUnitId(null);
    }
  };

  const validarModeracion = async (unidadNombre, situacionTexto, severity = "error") => {
    try {
      await ModeracionService.validarContenido({
        tema: unidadNombre || "",
        objetivo: situacionTexto || "",
        sub_area_tematica: "SAC",
      });
      return true;
    } catch (err) {
      showSnack(getModeracionMessage(err), severity);
      return false;
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const guardado = localStorage.getItem(STORAGE_KEY);
    if (guardado) {
      const data = JSON.parse(guardado);
      const cantidad = Number(data.cantidadUnidades ?? 8);
      const unidades = Array.isArray(data.unidadesBackend) ? data.unidadesBackend : [];
      const sel = Array.isArray(data.seleccionados) ? data.seleccionados.map(toKey) : [];
      const selRecortado = sel.length > cantidad ? sel.slice(0, cantidad) : sel;

      setCantidadUnidades(cantidad);
      setUnidadesBackend(unidades);
      setSeleccionados(selRecortado);
      setLoading(false);
      return;
    }

    const cargar = async () => {
      try {
        setLoading(true);
        const data = await GenerarUnidadesService.generarUnidades(12);
        setUnidadesBackend(data);
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ cantidadUnidades: 8, unidadesBackend: data, seleccionados: [] })
        );
      } catch (error) {
        console.error("Error cargando unidades:", error);
        showSnack(
          getApiErrorMessage(error, "No se pudo cargar la lista de unidades."),
          error?.response?.status === 409 ? "warning" : "error"
        );
      } finally {
        setLoading(false);
      }
    };

    cargar();
  }, []);

  useEffect(() => {
    if (loading || unidadesBackend.length === 0) return;
    persistUnitSelectionState();
  }, [loading, unidadesBackend, persistUnitSelectionState]);

  const toggleCheckbox = (id) => {
    const key = toKey(id);
    setSeleccionados((prev) => {
      const prevKeys = (prev || []).map(toKey);
      if (prevKeys.includes(key)) {
        return prevKeys.filter((item) => item !== key);
      }
      if (prevKeys.length < Number(cantidadUnidades)) {
        return [...prevKeys, key];
      }
      return prevKeys;
    });
  };

  const handleCantidadUnidadesChange = (nuevoValor) => {
    if (nuevoValor < cantidadUnidades && seleccionados.length > nuevoValor) {
      setSeleccionados((prev) => prev.slice(0, nuevoValor));
    }
    setCantidadUnidades(nuevoValor);
  };

  const handleContinuar = async () => {
    const selectedIds = (seleccionados || []).map(String);
    const seleccionSet = new Set(selectedIds);

    const seleccionFinal = unidadesBackend
      .filter((u) => seleccionSet.has(String(u.id)))
      .map((u, i) => ({ id: u.id, nombre: u.nombre, nroUnidad: i + 1, prioridad: "" }));

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ cantidadUnidades, unidadesBackend, seleccionados: selectedIds })
    );

    try {
      const savedStep5 = JSON.parse(localStorage.getItem(STORAGE_STEP5));
      const sameSelection =
        JSON.stringify(normalizeIds(savedStep5?.seleccionadosIds)) === JSON.stringify(normalizeIds(selectedIds));

      if (
        sameSelection &&
        savedStep5?.situacionGenerada &&
        Array.isArray(savedStep5?.tablaConsolidada) &&
        savedStep5.tablaConsolidada.length > 0
      ) {
        setUnidadesSeleccionadas(savedStep5.tablaConsolidada);
        onNext();
        return;
      }
    } catch (error) {
      console.warn("No se pudo reutilizar el estado guardado de situaciones:", error);
    }

    setGeneratingSituations(true);

    try {
      const titulos = seleccionFinal.map((u) => u.nombre);
      const res = await GenerarUnidadesService.generarSituacionSignificativa(titulos);
      const actualizadas = seleccionFinal.map((u, i) => ({ ...u, prioridad: res[i]?.situacion || "" }));

      for (const unidad of actualizadas) {
        const ok = await validarModeracion(unidad.nombre, unidad.prioridad, "warning");
        if (!ok) {
          setGeneratingSituations(false);
          return;
        }
      }

      localStorage.setItem(
        STORAGE_STEP5,
        JSON.stringify({
          tablaConsolidada: actualizadas,
          situacionGenerada: true,
          seleccionadosIds: (seleccionados || []).map(String),
          guardadoEnBackend: false,
        })
      );

      setUnidadesSeleccionadas(actualizadas);
      onNext();
    } catch (error) {
      console.error("Error generando situaciones significativas:", error);
      showSnack("No se pudieron generar las situaciones significativas.", "error");
    } finally {
      setGeneratingSituations(false);
    }
  };

  return {
    cantidadUnidades,
    closeSnack,
    generatingSituations,
    handleContinuar,
    handleCantidadUnidadesChange,
    loading,
    openRegenerateDialog,
    regenerateSingleUnit,
    regenerateUnitsList,
    regeneratingUnitId,
    regeneratingUnits,
    seleccionados,
    setOpenRegenerateDialog,
    snack,
    toKey,
    toggleCheckbox,
    unidadesBackend,
  };
};

export default useUnitSelectionStep;
